import { Pie } from "@ant-design/charts";
import { MoreOutlined } from "@ant-design/icons";
import { Dropdown, Menu } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import {
  DetailsWrapper,
  DropdownWrapper,
  Header,
  InsightsWrapper,
  LeftDetailsWrapper,
  MarkerItem,
  MarkerWrapper,
  RightDetailsWrapper,
  TitleWrapper,
  Wrapper,
  Marker,
  MarkerTitle,
  ButtonMarkerItem,
} from "./elements";
import { CSVLink } from "react-csv";
import { toJpeg, toCanvas } from "html-to-image";
import { jsPDF } from "jspdf";

type Props = {
  items: Array<{ type: string; value: number; percentage: number }>;
  onClickMenu?: (e: any) => void;
  dateRange?: any;
  onClickShowOthers: (e: any) => void;
  failedReasons?: any;
};

const FailedReasonInsights: FC<Props> = ({
  dateRange,
  onClickMenu,
  items,
  onClickShowOthers,
  failedReasons,
}) => {
  const csvRef = useRef<any>();
  const jpgRef = useRef<any>();
  const [csvData, setCsvData] = useState<Array<any>>([]);

  const filteredDate = dateRange
    ? `${dateRange[0].format("YYYY-MM-DD")} - ${dateRange[1].format(
      "YYYY-MM-DD"
    )}`
    : "";

  useEffect(() => {
    if (items.length) {
      const data = items.reduce((arr: any, item): any => {
        arr.push({
          firstRow: item.type,
          secondRow: "",
          thirdRow: `${item.percentage}%`,
        });
        return arr;
      }, []);

      setCsvData(data);
    }
  }, [items]);

  const handleClickMenu = (e: any) => {
    if (onClickMenu) {
      onClickMenu(e);
    } else {
      switch (e.key) {
        case "CSV":
          csvRef.current?.link.click();
          break;
        case "JPG":
          toJpeg(jpgRef.current, { cacheBust: true })
            .then(url => {
              const link = document.createElement("a");
              link.download = `FailedReasons.jpg`;
              link.href = url;
              link.click();
            })
            .catch(err => {
              console.log(err);
            });
          break;
        default:
          toCanvas(jpgRef.current).then(canvas => {
            const image = canvas.toDataURL("image/jpeg", 1.0);
            const doc = new jsPDF("p", "px", "a4");
            doc.addImage(image, "JPEG", 10, 10, 0, 0);
            doc.save(`FailedReasons.pdf`);
          });
          break;
      }
    }
  };

  const dropdownMenu = (
    <Menu onClick={handleClickMenu}>
      <Menu.Item key="JPG">Download JPG</Menu.Item>
      <Menu.Item key="PDF">Download PDF</Menu.Item>
      <Menu.Item key="CSV">Download CSV</Menu.Item>
    </Menu>
  );

  const colors = ["#F2C94C", "#56CCF2", "#BB6BD9", "#2D9CDB", "#F2994A", "#219653"];

  const chartConfig = {
    width: 120,
    height: 120,
    autoFit: false,
    data: items,
    angleField: "value",
    colorField: "type",
    color: colors,
    radius: 0.9,
    label: {
      type: "inner",
      labelEmit: false,
      style: { opacity: 0 }
    }
  };

  return (
    <Wrapper ref={jpgRef}>
      <InsightsWrapper>
        <Header>
          <TitleWrapper>Failed reasons</TitleWrapper>
          <DropdownWrapper>
            <Dropdown
              trigger={["click"]}
              overlay={dropdownMenu}
              placement="bottomLeft"
            >
              <MoreOutlined
                rotate={90}
                style={{ fontSize: "26px", color: "#000000" }}
              />
            </Dropdown>
          </DropdownWrapper>
        </Header>
        <DetailsWrapper>
          <RightDetailsWrapper>
            <Pie
              {...chartConfig}
              legend={false}
              tooltip={false}
              style={{ width: "100%", height: "inherit" }}
            />
          </RightDetailsWrapper>
          <LeftDetailsWrapper>
            <MarkerWrapper>
              {items.map(({ type, percentage }, index) =>
                index === items.length - 1 ? (
                  <MarkerItem key={index}>
                    <Marker color={colors[index]} />
                    <ButtonMarkerItem onClick={onClickShowOthers}>{`${type} (${
                      percentage || 0
                    }%)`}</ButtonMarkerItem>
                  </MarkerItem>
                ) : (
                  <MarkerItem key={index}>
                    <Marker color={colors[index]} />
                    <MarkerTitle>{`${type} (${percentage || 0}%)`}</MarkerTitle>
                  </MarkerItem>
                )
              )}
            </MarkerWrapper>
          </LeftDetailsWrapper>
        </DetailsWrapper>
      </InsightsWrapper>

      <CSVLink
        style={{ visibility: "hidden" }}
        data={[
          { firstRow: "Date Range: ", secondRow: filteredDate, thirdRow: "" },
          {},
          { firstRow: "Reason", secondRow: "", thirdRow: "Percentage" },
          ...csvData,
          ...failedReasons?.map((item: string) => ({
            firstRow: "",
            secondRow: item,
            thirdRow: "",
          })),
        ]}
        headers={[
          { key: "firstRow", label: "" },
          { key: "secondRow", label: "" },
          { key: "thirdRow", label: "" },
        ]}
        filename="Failed Reasons.csv"
        ref={csvRef}
      />
    </Wrapper>
  );
};

export default FailedReasonInsights;
